import React, { useContext } from 'react';

import { AppContext } from '@prisjakt/ui-app-context';
import Currency from '@prisjakt/ui-currency';
import Lazy from '@prisjakt/ui-lazy';
import Rating from '@prisjakt/ui-rating';
import { Row } from '@prisjakt/ui/list/variants';
import {
  TitleSmallText,
  BodySmallText,
  FontEmphasis,
} from '@prisjakt/ui/text/variants';
import { CountTranslation } from '@prisjakt/ui-rating/src/types';
import { Price } from '../price';

import { Product } from '../../../../../src/types';
import { BaseProps } from '../../types';

import * as S from './styles';

const RATING_TRANSLATION = {
  domain: 'product',
  key: 'product-card.n-ratings',
} as CountTranslation;

type Props = Omit<BaseProps, 'category'> & {
  onClick?: (productId: string | number) => void;
  priceRow?: Product['cheapestPrice'];
};

export const ProductCompactCard = (props: Props) => {
  const {
    className,
    diffPercentage = 0,
    fallbackImageSrc,
    highlightedName,
    imageSrc,
    id,
    name,
    link,
    onClick,
    price,
    priceInclShipping,
    rating,
    reviewsCount,
    selectable,
    selected = false,
    onDeselect = () => {},
    onSelect = () => {},
  } = props;

  const { t } = useContext(AppContext);

  const handleSelect = (productId?: number) => {
    if (!productId) return;

    if (selected) {
      onDeselect();
    } else {
      onSelect();
    }
  };

  const handleOnClick = (productId?: number) => {
    if (!productId) return;
    if (onClick) onClick(productId);
  };

  const ListItemContent = (
    <>
      <S.CheckboxAndImageColumn>
        {selectable ? (
          <S.CompareCheckbox selected={selected} aria-label={name} />
        ) : null}
        <Lazy offset={0}>
          <S.ImageContainer>
            <S.ListImage
              name={name}
              src={imageSrc || ''}
              fallback={fallbackImageSrc}
            />
          </S.ImageContainer>
        </Lazy>
      </S.CheckboxAndImageColumn>
      <div>
        <TitleSmallText>{highlightedName || name}</TitleSmallText>
        {rating && rating > 0 ? (
          <Rating
            rate={rating}
            singleStar
            showRate
            showCounter
            count={reviewsCount}
            countTranslation={RATING_TRANSLATION}
            size="small"
          />
        ) : null}
      </div>
      <S.PriceContainer>
        <Row>
          {price && (
            <S.PriceTextWrapper>
              <S.PriceText>
                {diffPercentage ? (
                  <S.DiffPercentage>
                    -{Math.round(diffPercentage)}%
                  </S.DiffPercentage>
                ) : null}

                <div>
                  <BodySmallText emphasis={FontEmphasis.LOW}>
                    {t('product', 'price.from-abbr')}
                  </BodySmallText>{' '}
                  <Price price={price} isDeal={Boolean(diffPercentage)} />
                </div>
              </S.PriceText>
              {priceInclShipping ? (
                <BodySmallText emphasis={FontEmphasis.LOW}>
                  {t('product', 'price.including-shipping', {
                    price: <Currency price={priceInclShipping} />,
                  })}
                </BodySmallText>
              ) : null}
            </S.PriceTextWrapper>
          )}
        </Row>
      </S.PriceContainer>
    </>
  );

  return (
    <S.ListItem
      className={className}
      data-test="ProductCompactListCard"
      isSelectingProducts={selectable}
      key={id}
    >
      <S.ProductLink
        to={selectable ? undefined : link}
        onClick={selectable ? () => handleSelect(id) : () => handleOnClick(id)}
      >
        {ListItemContent}
      </S.ProductLink>
    </S.ListItem>
  );
};
